import { z } from 'zod'

/** State of one document on one estate. */
export const documentStateSchema = z.enum(['done', 'open', 'not_applicable'])

/**
 * A document type, i.e. one column of the matrix.
 * Sent once at the top level rather than repeated on every estate — with 23
 * documents across ~90 estates that is the difference between a lean payload
 * and 2 000 copies of the same label.
 */
export const checklistDocumentSchema = z.object({
  /** onOffice checkbox field name — stable, used as the map key. */
  key: z.string(),
  label: z.string(),
  /** How many listed estates are still missing this document. */
  openCount: z.number().int().nonnegative(),
})

export const estateStepSchema = z.object({
  state: documentStateSchema,
  note: z.string().nullable(),
})

export const estateRowSchema = z.object({
  estateId: z.number().int(),
  /** `objektnr_extern`, shown in onOffice as "ImmoNr". */
  externalNumber: z.string().nullable(),
  title: z.string().nullable(),
  /** `status2` — marketing phase, e.g. "Aktive Vermarktung". */
  status: z.string().nullable(),
  /** `status` — Aktiv / Inaktiv. */
  activeState: z.string().nullable(),
  agent: z.string().nullable(),
  type: z.string().nullable(),
  /** Kauf / Miete / Erbpacht. */
  marketingType: z.string().nullable(),
  /** Set when the object has been sold. */
  soldAt: z.iso.datetime().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  /** When the object was created in onOffice (`erstellt_am`). */
  createdAt: z.iso.datetime().nullable(),
  /** Last modification in onOffice (`geaendert_am`). */
  modifiedAt: z.iso.datetime().nullable(),

  openCount: z.number().int().nonnegative(),
  doneCount: z.number().int().nonnegative(),
  notApplicableCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),

  /** Keyed by `checklistDocumentSchema.key`. */
  steps: z.record(z.string(), estateStepSchema),
})

export const checklistOverviewSchema = z.object({
  documents: z.array(checklistDocumentSchema),
  estates: z.array(estateRowSchema),
  summary: z.object({
    estatesTracked: z.number().int().nonnegative(),
    estatesWithOpenSteps: z.number().int().nonnegative(),
    estatesComplete: z.number().int().nonnegative(),
    openSteps: z.number().int().nonnegative(),
    doneSteps: z.number().int().nonnegative(),
    totalSteps: z.number().int().nonnegative(),
  }),
  /** Distinct values present in the data, for building filter dropdowns. */
  facets: z.object({
    status: z.array(z.string()),
    agent: z.array(z.string()),
    type: z.array(z.string()),
    city: z.array(z.string()),
    marketingType: z.array(z.string()),
    activeState: z.array(z.string()),
  }),
  lastSync: z
    .object({
      status: z.enum(['running', 'succeeded', 'failed']),
      startedAt: z.iso.datetime(),
      finishedAt: z.iso.datetime().nullable(),
      recordCount: z.number().int(),
      error: z.string().nullable(),
    })
    .nullable(),
})

/**
 * One photo of an estate, mirrored from onOffice.
 *
 * `url` carries NO size suffix. The CDN resizes on demand, so a consumer
 * appends `@<width>x<height>` for the variant it needs rather than the cache
 * committing to one — see `sizedImageUrl` in the web app.
 */
export const estateImageSchema = z.object({
  /** onOffice file id. */
  id: z.number().int(),
  /** onOffice's own category — Titelbild, Foto, Grundriss, ... */
  category: z.string(),
  url: z.string(),
  title: z.string().nullable(),
})

/** Full attributes of one estate, fetched only when a row is opened. */
export const estateDetailSchema = z.object({
  estateId: z.number().int(),
  externalNumber: z.string().nullable(),
  title: z.string().nullable(),
  status: z.string().nullable(),
  marketingType: z.string().nullable(),
  agent: z.string().nullable(),
  createdAt: z.iso.datetime().nullable(),
  modifiedAt: z.iso.datetime().nullable(),
  soldAt: z.iso.datetime().nullable(),
  syncedAt: z.iso.datetime(),
  /** Titelbild first, then Fotos, then Grundrisse. Empty when none exist. */
  images: z.array(estateImageSchema),
  /** Label/value pairs grouped for display; empty groups are omitted. */
  groups: z.array(
    z.object({
      title: z.string(),
      entries: z.array(z.object({ label: z.string(), value: z.string() })),
    }),
  ),
})

export const syncResultSchema = z.object({
  estates: z.number().int(),
  estatesFetched: z.number().int(),
  documents: z.number().int(),
  checklistItems: z.number().int(),
  images: z.number().int(),
  openItems: z.number().int(),
  notApplicableItems: z.number().int(),
  durationMs: z.number().int(),
})

export type EstateDetail = z.infer<typeof estateDetailSchema>
export type EstateImage = z.infer<typeof estateImageSchema>
export type DocumentState = z.infer<typeof documentStateSchema>
export type ChecklistDocument = z.infer<typeof checklistDocumentSchema>
export type EstateStep = z.infer<typeof estateStepSchema>
export type EstateRow = z.infer<typeof estateRowSchema>
export type ChecklistOverview = z.infer<typeof checklistOverviewSchema>
export type SyncResult = z.infer<typeof syncResultSchema>
